import React from 'react';
import PropTypes from 'prop-types';
import CategorizedLinks from '../CategorizedLinks/CategorizedLinks';
import Button from '../Button/Button';
import { vercel_base_url } from '../../util/config';

import * as Styled from './Footer.styled';
import FooterEmail from '../FooterEmail/FooterEmail';

/**
 * The footer appears at the bottom of the page and includes an email input, list of social media, and categorized lists of links.
 */
const Footer = ({
  categorizedLinks,
  happinessModule,
  legal,
  notice,
  ...props
}) => {
  let phone;

  if (happinessModule?.phone) {
    phone = (
      <Styled.PhoneContainer>
        {happinessModule.phone.text}
        <Styled.PhoneNumber href={`tel:${happinessModule.phone.url}`}>
          {happinessModule.phone.url}
        </Styled.PhoneNumber>
      </Styled.PhoneContainer>
    );
  }

  return (
    <Styled.Footer data-track-region="footer">
      <FooterEmail {...props} />
      <Styled.LowerGrid>
        <Styled.HappinessContainer categorizedLinks={categorizedLinks.length}>
          <Styled.HappinessIcon name="happiness-guarantee" />
          <Styled.HappinessBody>{happinessModule.text}</Styled.HappinessBody>
          <Styled.ButtonWrapper>
            {happinessModule.buttons.map((button, index) => (
              <Button
                as="a"
                variant="defaultDark"
                size="small"
                href={button.url}
                key={index}
              >
                {button.text}
              </Button>
            ))}
          </Styled.ButtonWrapper>
          {phone}
        </Styled.HappinessContainer>
        <Styled.Divider />
        {categorizedLinks.map((category, index) => (
          <Styled.LinksContainer
            key={index}
            length={categorizedLinks.length}
            column={index}
          >
            <CategorizedLinks title={category.text} links={category.links} />
          </Styled.LinksContainer>
        ))}
        <Styled.BCorpContainer categorizedLinks={categorizedLinks.length}>
          <Styled.BCorpIcon
            categorizedLinks={categorizedLinks.length}
            name="b-corp"
          />
          <Styled.BCorpText categorizedLinks={categorizedLinks.length}>
            Bombas is a Certified <br /> B Corporation
            <br />
            <Styled.BCorpLink
              href={`${vercel_base_url}/pages/b-corp-certified`}
              data-track-label="bcorp-link"
            >
              Learn More
            </Styled.BCorpLink>
          </Styled.BCorpText>
        </Styled.BCorpContainer>
        <Styled.LegalContainer>
          <Styled.LegalLinks aria-label="privacy and data">
            {legal.map((link, index) => (
              <Styled.LegalLink
                href={link.url}
                data-track-label={link.text}
                key={index}
              >
                {link.text}
              </Styled.LegalLink>
            ))}
            <Styled.LegalLink
              href="#"
              onClick={() => window.Optanon.ToggleInfoDisplay()}
            >
              Cookie Settings
            </Styled.LegalLink>
          </Styled.LegalLinks>
        </Styled.LegalContainer>
        <Styled.Notice>
          <Styled.NoticeTitle>{notice.header}</Styled.NoticeTitle>
          <Styled.NoticeMessage>{notice.html}</Styled.NoticeMessage>
          <Styled.NoticePrivacyLink
            href={`${vercel_base_url}/pages/privacy-policy`}
            data-track-label="privacy policy notice"
          >
            Privacy Policy
          </Styled.NoticePrivacyLink>
          .
        </Styled.Notice>
      </Styled.LowerGrid>
    </Styled.Footer>
  );
};

Footer.propTypes = {
  /**
   * List of links with a category title that appear in the footer.
   */
  categorizedLinks: PropTypes.arrayOf(Object).isRequired,
  /**
   * Text, images, and links that appear in the happiness section of the footer.
   */
  happinessModule: PropTypes.instanceOf(Object).isRequired,
  /**
   * Text, images, and links that appear in the legal section of the footer.
   */
  legal: PropTypes.instanceOf(Object).isRequired,
  /**
   * Text and links that appear in the notice section of the footer.
   */
  notice: PropTypes.instanceOf(Object)
};

Footer.defaultProps = {
  notice: {
    title: 'Notice to California Consumers:',
    body: 'Bombas may collect "Identifiers", "Commercial information", "Internet or other electronic network activity", and/or "Geolocation data" when you visit this website, and may use such information to draw inferences and for other operational and commercial purposes. For more information, please see our '
  }
};
Footer.displayName = 'Footer';

export default Footer;
